import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getUpcomingEvents,
  type ApiUpcomingEvent,
} from '@/src/infrastructure/lib/api';

/**
 * UpcomingEventsCard — compact list of the next N events for the dashboard.
 *
 * Talks to /calendar/upcoming so the dashboard does NOT have to read the
 * Invitation table directly. Keeping this widget driven by the projection
 * means cancelled / archived invitations vanish without a code change here.
 */
export function UpcomingEventsCard({ limit = 5 }: { limit?: number }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState<ApiUpcomingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');

    getUpcomingEvents(limit)
      .then((data) => {
        if (active) setEvents(data);
      })
      .catch((err) => {
        if (active) {
          setError(
            err instanceof Error ? err.message : 'Failed to load upcoming events.',
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [limit]);

  return (
    <section className="rounded-xl border border-outline-variant bg-surface-low p-4 md:p-6">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-on-surface">Upcoming events</h3>
          <p className="text-sm text-on-surface-variant">
            Your next {limit} invitations, soonest first.
          </p>
        </div>
      </header>

      {loading ? (
        <UpcomingSkeleton rows={Math.min(limit, 3)} />
      ) : error ? (
        <p className="text-status-failed text-sm">{error}</p>
      ) : events.length === 0 ? (
        <p className="text-on-surface-variant text-sm">
          No upcoming events. Publish an invitation to see it here.
        </p>
      ) : (
        <ul className="divide-y divide-outline-variant">
          {events.map((e) => (
            <li
              key={e.id}
              className="py-3 flex items-center justify-between gap-4 cursor-pointer hover:bg-surface-low rounded-md px-2 -mx-2"
              onClick={() => navigate(`/invitations/${e.invitationId}`)}
            >
              <div className="min-w-0">
                <div className="font-medium text-on-surface truncate">{e.title}</div>
                <div className="text-xs text-on-surface-variant truncate">
                  {new Date(e.startDatetime).toLocaleString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                  {e.venue ? ` · ${e.venue}` : ''}
                </div>
              </div>
              <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary whitespace-nowrap">
                {e.daysUntil === 0 ? 'Today' : `In ${e.daysUntil} day${e.daysUntil === 1 ? '' : 's'}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function UpcomingSkeleton({ rows }: { rows: number }) {
  return (
    <ul className="divide-y divide-outline-variant animate-pulse">
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className="py-3 flex items-center justify-between gap-4">
          <div className="flex-1">
            <div className="h-4 w-2/3 bg-surface-low rounded mb-2" />
            <div className="h-3 w-1/2 bg-surface-low rounded" />
          </div>
          <div className="h-6 w-16 rounded-full bg-surface-low" />
        </li>
      ))}
    </ul>
  );
}
